'use client';

import { type ReactNode } from 'react';

import { Body, Heading } from '@depop/web-thrift-ui/Typography';
import { Drawer } from '@depop/web-ui-kit/Drawer';
import { Modal } from '@depop/web-ui-kit/Modal';
import { useTranslations } from 'next-intl';

import { ButtonExperiment } from '../ButtonExperiment';
import { ExtendedLink } from '../ExtendedLink';
import { Divider } from '../OrDivider';

import styles from './styles.module.css';

import { MOBILE_MEDIA_QUERY } from '@/constants/mobileMediaQuery';
import { useMediaQuery } from '@/utils/useMediaQuery';
import { useSheetDragToDismiss } from '@/utils/useSheetDragToDismiss';

type Props = {
  id: string;
  isOpen: boolean;
  setOpen: (isOpen: boolean) => void;
  title: string;
  description: string;
  illustration: ReactNode;
  trackingId: string;
  redirectParam: string;
  handleSignupClick?: () => void;
  handleLoginClick?: () => void;
  handleDismissClick?: () => void;
};

export function SignupGateModal({
  id,
  isOpen,
  setOpen,
  title,
  description,
  illustration,
  redirectParam,
  handleSignupClick,
  handleLoginClick,
  handleDismissClick,
}: Props) {
  function handleDismiss() {
    handleDismissClick?.();
    setOpen(false);
  }

  const { handleRef, dragHandlers } = useSheetDragToDismiss(handleDismiss);
  const t = useTranslations('common');
  const isMobileBreakpoint = useMediaQuery(MOBILE_MEDIA_QUERY);

  function renderContent() {
    return (
      <div className={styles.container}>
        <div className={styles.illustration}>{illustration}</div>
        <div className={styles.text}>
          <Heading variant="lg" as="h2">
            {title}
          </Heading>
          <Body variant="sm-regular">{description}</Body>
        </div>
        <div className={styles.content}>
          <ExtendedLink
            href={`/signup/?redirect=${redirectParam}`}
            onClick={handleSignupClick}
            className={styles.signupButton}
          >
            <ButtonExperiment as="span" fullWidth>
              {t('SignupGate.Signup')}
            </ButtonExperiment>
          </ExtendedLink>
          <Body>
            {t.rich('SignupGate.Login', {
              login: (children) => (
                <ExtendedLink
                  href={`/login/?redirect=${redirectParam}`}
                  onClick={handleLoginClick}
                >
                  <Body
                    as="span"
                    variant="sm-bold"
                    className={styles.loginLink}
                  >
                    {children}
                  </Body>
                </ExtendedLink>
              ),
            })}
          </Body>
          <Divider className={styles.divider} />
          <ButtonExperiment
            variant="secondary"
            fullWidth
            onClick={handleDismiss}
          >
            {t('SignupGate.NotNow')}
          </ButtonExperiment>
        </div>
      </div>
    );
  }

  if (isMobileBreakpoint) {
    return (
      <Drawer
        position="bottom"
        isPartialBottomDrawer
        isOpen={isOpen}
        setOpen={setOpen}
        onClose={handleDismissClick}
        label={title}
        className={styles.sheet}
        customOverrides={{
          header: (
            <span
              ref={handleRef}
              className={styles.grabber}
              aria-hidden="true"
              {...dragHandlers}
            />
          ),
        }}
        content={renderContent}
      />
    );
  }

  return (
    <Modal
      id={id}
      isOpen={isOpen}
      setOpen={setOpen}
      onCloseModal={handleDismissClick}
      label={title}
      overlayAriaLabel={title}
      className={styles.modal}
      customOverrides={{
        fullscreenAtSmallBreakpoint: true,
      }}
      content={renderContent}
    />
  );
}
